import { useEffect, useRef, useState } from 'react';
import { customLocationAdminApi } from '../../api/customLocationAdminApi';
import {
  formatCustomLocationStatus,
  customLocationStatusClassName,
} from './travelManageConstants';
import { createRequestGuard, toSafeErrorMessage } from './adminPageUtils';

function isConflictError(err) {
  if (!err) return false;
  if (err.response?.status === 409 || err.status === 409 || err.code === 95903) return true;
  const msg = String(err.message || '').toLowerCase();
  return msg.includes('conflict') || msg.includes('409') || msg.includes('版本');
}

function formatDateTime(value) {
  if (!value) return '—';
  return String(value).replace('T', ' ').slice(0, 19);
}

function formatCaseStatus(status) {
  if (!status) return '—';
  const map = {
    OPEN: '待處理',
    RESOLVED_KEEP: '已維持上架',
    RESOLVED_REMOVE: '已下架',
    CLOSED: '已結案',
  };
  return map[status] || status;
}

export default function CustomLocationReviewPage() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [conflictError, setConflictError] = useState(false);
  const [actionTarget, setActionTarget] = useState(null); // { action: 'keep'|'remove', item }
  const [removeReason, setRemoveReason] = useState('');
  const [removeError, setRemoveError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const requestGuard = useRef(createRequestGuard()).current;

  async function loadQueue() {
    const token = requestGuard.next();
    setLoading(true);
    setError('');
    setConflictError(false);
    try {
      const data = await customLocationAdminApi.governanceQueue({ limit: 50 });
      if (!requestGuard.isCurrent(token)) return;
      setCases(Array.isArray(data) ? data : data?.records || []);
    } catch (err) {
      if (!requestGuard.isCurrent(token)) return;
      if (isConflictError(err)) {
        setConflictError(true);
      } else {
        setError(toSafeErrorMessage(err, '載入治理佇列失敗，請稍後重試'));
      }
      setCases([]);
    } finally {
      if (requestGuard.isCurrent(token)) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleRefresh() {
    setError('');
    setMessage('');
    setConflictError(false);
    loadQueue();
  }

  function closeActionModal() {
    setActionTarget(null);
    setRemoveReason('');
    setRemoveError('');
    setSubmitting(false);
  }

  async function handleConfirmAction() {
    if (!actionTarget || submitting) return;
    const { action, item } = actionTarget;

    if (action === 'remove') {
      const trimmed = removeReason.trim();
      if (!trimmed) {
        setRemoveError('下架原因必填');
        return;
      }
      if (trimmed.length > 500) {
        setRemoveError('下架原因不可超過 500 字');
        return;
      }
    }

    setSubmitting(true);
    setError('');
    setConflictError(false);

    try {
      const id = String(item.id);
      if (action === 'keep') {
        await customLocationAdminApi.keepPublished(id, { expectedRowVersion: item.rowVersion });
        setMessage(`自訂地點 #${id} 已維持上架，相關檢舉已結案`);
      } else {
        await customLocationAdminApi.remove(id, {
          expectedRowVersion: item.rowVersion,
          reason: removeReason.trim(),
        });
        setMessage(`自訂地點 #${id} 已下架`);
      }
      closeActionModal();
      loadQueue();
    } catch (err) {
      if (isConflictError(err)) {
        setConflictError(true);
        closeActionModal();
        loadQueue();
      } else {
        setError(toSafeErrorMessage(err, '操作失敗，請稍後重試'));
        setSubmitting(false);
      }
    }
  }

  return (
    <div className="page-card" data-testid="custom-location-governance-page">
      <div
        className="section-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div>
          <h2>自訂地點治理</h2>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: '13px' }}>
            此處處理使用者檢舉與平台治理，不是公開前審核。
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleRefresh}
          disabled={loading}
          data-testid="refresh-btn"
        >
          重新整理
        </button>
      </div>

      {conflictError ? (
        <div
          className="alert alert-warning alert-conflict"
          role="alert"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}
          data-testid="conflict-alert"
        >
          <span>資料已被更新（版本衝突），請重新整理以取得最新資料。</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleRefresh}>
            立即重新整理
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="alert alert-danger" role="alert" style={{ marginBottom: '16px' }} data-testid="error-msg">
          {error}
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={handleRefresh}>
            重試
          </button>
        </div>
      ) : null}
      {message ? (
        <div className="alert alert-success" role="alert" style={{ marginBottom: '16px' }} data-testid="success-msg">
          {message}
        </div>
      ) : null}

      <div className="table-responsive">
        <table className="admin-table" data-testid="location-governance-table">
          <thead>
            <tr>
              <th>地點 ID</th>
              <th>名稱</th>
              <th>狀態</th>
              <th>案件狀態</th>
              <th>檢舉數</th>
              <th>檢舉原因</th>
              <th>首次檢舉</th>
              <th>最近檢舉</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center' }} data-testid="loading-row">載入中…</td>
              </tr>
            ) : cases.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center' }} data-testid="empty-list">
                  目前沒有待處理的自訂地點檢舉
                </td>
              </tr>
            ) : (
              cases.map((loc) => (
                <tr key={String(loc.id)} data-testid={`location-row-${loc.id}`}>
                  <td>{String(loc.id)}</td>
                  <td>{loc.name || '—'}</td>
                  <td>
                    <span className={`badge ${customLocationStatusClassName(loc.status)}`}>
                      {formatCustomLocationStatus(loc.status)}
                    </span>
                  </td>
                  <td data-testid="case-status">{formatCaseStatus(loc.caseStatus)}</td>
                  <td data-testid="report-count">{loc.reportCount ?? 0}</td>
                  <td data-testid="report-reasons">
                    {Array.isArray(loc.reportReasons) && loc.reportReasons.length
                      ? loc.reportReasons.join('、')
                      : '—'}
                  </td>
                  <td data-testid="first-reported-at">{formatDateTime(loc.firstReportedAt)}</td>
                  <td data-testid="last-reported-at">{formatDateTime(loc.lastReportedAt)}</td>
                  <td style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => setActionTarget({ action: 'keep', item: loc })}
                      data-testid="keep-btn"
                    >
                      維持上架
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => {
                        setActionTarget({ action: 'remove', item: loc });
                        setRemoveReason('');
                        setRemoveError('');
                      }}
                      data-testid="remove-btn"
                    >
                      下架
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {actionTarget?.action === 'keep' ? (
        <div className="admin-modal-overlay" role="presentation" onClick={closeActionModal}>
          <div className="admin-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} data-testid="keep-modal">
            <div className="admin-modal__header">
              <h3>確認維持上架</h3>
              <button type="button" className="admin-modal__close" onClick={closeActionModal}>×</button>
            </div>
            <div className="admin-modal__body">
              <p>確定維持此地點公開，並結案相關檢舉？</p>
              <p style={{ color: '#666', fontSize: '13px' }}>
                地點：{actionTarget.item.name}（#{String(actionTarget.item.id)}）
              </p>
            </div>
            <div className="admin-modal__footer">
              <button type="button" className="btn btn-secondary" onClick={closeActionModal} disabled={submitting}>取消</button>
              <button type="button" className="btn btn-success" onClick={handleConfirmAction} disabled={submitting} data-testid="confirm-keep-btn">
                {submitting ? '提交中…' : '確認維持上架'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {actionTarget?.action === 'remove' ? (
        <div className="admin-modal-overlay" role="presentation" onClick={closeActionModal}>
          <div className="admin-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} data-testid="remove-modal">
            <div className="admin-modal__header">
              <h3>確認下架</h3>
              <button type="button" className="admin-modal__close" onClick={closeActionModal}>×</button>
            </div>
            <div className="admin-modal__body">
              <p style={{ marginBottom: '8px' }}>
                地點：{actionTarget.item.name}（#{String(actionTarget.item.id)}）
              </p>
              <label htmlFor="clRemoveReason">下架原因（必填；上限 500 字）</label>
              <textarea
                id="clRemoveReason"
                className="form-control"
                rows="4"
                value={removeReason}
                onChange={(e) => {
                  setRemoveReason(e.target.value);
                  if (removeError) setRemoveError('');
                }}
                data-testid="remove-reason-input"
              />
              {removeError ? (
                <span className="text-danger" style={{ fontSize: '12px', color: '#dc3545' }} data-testid="remove-error">
                  {removeError}
                </span>
              ) : null}
            </div>
            <div className="admin-modal__footer">
              <button type="button" className="btn btn-secondary" onClick={closeActionModal} disabled={submitting}>取消</button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmAction} disabled={submitting} data-testid="confirm-remove-btn">
                {submitting ? '提交中…' : '確認下架'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
